import {
  type ApprovalRequestReadModel,
  isRetryApprovalExecutionCommand,
  type RetryApprovalExecutionCommand,
} from "@billiard/contracts";

export type ApprovalExecutionRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ApprovalExecutionRecovery {
  tenantId: string;
  storeId: string;
  accountId: string;
  approvalId: string;
  expectedCaseVersion: number;
  idempotencyKey: string;
  state: ApprovalExecutionRecoveryState;
}

export type ApprovalExecutionRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ApprovalExecutionRecovery }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY =
  "billiard-erp:approval-execution-recovery:v1";

const pendingByIdentity = new Map<string, ApprovalExecutionRecovery>();
const dispatchingByIdentity = new Map<string, ApprovalExecutionRecovery>();
let hydrated = false;
let unavailable = false;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    [...value].length <= maximum
  );
}

function validState(value: unknown): value is ApprovalExecutionRecoveryState {
  return value === "CLAIMED" || value === "OUTCOME_UNKNOWN";
}

function commandFromRecovery(recovery: ApprovalExecutionRecovery): RetryApprovalExecutionCommand {
  return {
    approvalId: recovery.approvalId,
    expectedCaseVersion: recovery.expectedCaseVersion,
    idempotencyKey: recovery.idempotencyKey,
  };
}

function validRecovery(value: unknown): value is ApprovalExecutionRecovery {
  if (
    !record(value) ||
    !exactKeys(value, [
      "tenantId",
      "storeId",
      "accountId",
      "approvalId",
      "expectedCaseVersion",
      "idempotencyKey",
      "state",
    ]) ||
    !bounded(value.tenantId, 512) ||
    !bounded(value.storeId, 512) ||
    !bounded(value.accountId, 512) ||
    !validState(value.state)
  ) {
    return false;
  }
  return isRetryApprovalExecutionCommand({
    approvalId: value.approvalId,
    expectedCaseVersion: value.expectedCaseVersion,
    idempotencyKey: value.idempotencyKey,
  });
}

function frozen(
  operator: { tenantId: string; storeId: string; id: string },
  command: RetryApprovalExecutionCommand,
  state: ApprovalExecutionRecoveryState,
): ApprovalExecutionRecovery {
  return Object.freeze({
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    accountId: operator.id,
    approvalId: command.approvalId,
    expectedCaseVersion: command.expectedCaseVersion,
    idempotencyKey: command.idempotencyKey,
    state,
  });
}

function withState(
  recovery: ApprovalExecutionRecovery,
  state: ApprovalExecutionRecoveryState,
): ApprovalExecutionRecovery {
  return Object.freeze({ ...recovery, state });
}

function parsed(value: unknown): ApprovalExecutionRecovery | null {
  if (
    !record(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !validRecovery(value.recovery)
  ) {
    return null;
  }
  return Object.freeze({ ...value.recovery });
}

function same(left: ApprovalExecutionRecovery, right: ApprovalExecutionRecovery): boolean {
  return (
    left.tenantId === right.tenantId &&
    left.storeId === right.storeId &&
    left.accountId === right.accountId &&
    left.approvalId === right.approvalId &&
    left.expectedCaseVersion === right.expectedCaseVersion &&
    left.idempotencyKey === right.idempotencyKey &&
    left.state === right.state
  );
}

function belongsTo(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  return (
    recovery.tenantId === operator.tenantId &&
    recovery.storeId === operator.storeId &&
    recovery.accountId === operator.id
  );
}

function identityKey(operator: { tenantId: string; storeId: string; id: string }): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function recoveryIdentityKey(recovery: ApprovalExecutionRecovery): string {
  return JSON.stringify([recovery.tenantId, recovery.storeId, recovery.accountId]);
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): readonly ApprovalExecutionRecovery[] | null {
  const encoded = target.getItem(APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY);
  if (encoded === null) return null;
  try {
    const value = JSON.parse(encoded) as unknown;
    const legacy = parsed(value);
    if (legacy) return [legacy];
    if (
      !record(value) ||
      !exactKeys(value, ["version", "recoveries"]) ||
      value.version !== 2 ||
      !Array.isArray(value.recoveries) ||
      value.recoveries.length > 32 ||
      !value.recoveries.every(validRecovery)
    ) {
      return null;
    }
    const recoveries = value.recoveries.map((recovery) => Object.freeze({ ...recovery }));
    const keys = recoveries.map(recoveryIdentityKey);
    return new Set(keys).size === keys.length ? recoveries : null;
  } catch {
    return null;
  }
}

function sorted(recoveries: readonly ApprovalExecutionRecovery[]): ApprovalExecutionRecovery[] {
  return [...recoveries].sort((left, right) =>
    recoveryIdentityKey(left).localeCompare(recoveryIdentityKey(right)),
  );
}

function sameRecoveries(
  left: readonly ApprovalExecutionRecovery[],
  right: readonly ApprovalExecutionRecovery[],
): boolean {
  const sortedLeft = sorted(left);
  const sortedRight = sorted(right);
  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((recovery, index) => {
      const candidate = sortedRight[index];
      return candidate !== undefined && same(recovery, candidate);
    })
  );
}

function saveAll(recoveries: readonly ApprovalExecutionRecovery[]): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    if (recoveries.length === 0) {
      target.removeItem(APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY);
      return target.getItem(APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY) === null;
    }
    target.setItem(
      APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 2, recoveries: sorted(recoveries) }),
    );
    const stored = readStored(target);
    return stored !== null && sameRecoveries(stored, recoveries);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  const stored = readStored(target);
  if (!stored) {
    try {
      if (target.getItem(APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY) !== null) unavailable = true;
    } catch {
      unavailable = true;
    }
    return;
  }
  const recovered = stored.map((recovery) =>
    recovery.state === "CLAIMED" ? withState(recovery, "OUTCOME_UNKNOWN") : recovery,
  );
  if (!saveAll(recovered)) {
    unavailable = true;
    return;
  }
  for (const recovery of recovered) {
    pendingByIdentity.set(recoveryIdentityKey(recovery), recovery);
  }
}

export function readApprovalExecutionRecovery(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): ApprovalExecutionRecoveryView {
  hydrate();
  if (unavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  const recovery = pendingByIdentity.get(identityKey(operator));
  return recovery && belongsTo(recovery, operator)
    ? { status: "OWNED", recovery }
    : { status: "NONE" };
}

export function claimApprovalExecutionRecovery(
  operator: { tenantId: string; storeId: string; id: string },
  command: RetryApprovalExecutionCommand,
): ApprovalExecutionRecoveryView {
  const current = readApprovalExecutionRecovery(operator);
  if (current.status !== "NONE") return current;
  if (
    !bounded(operator.tenantId, 512) ||
    !bounded(operator.storeId, 512) ||
    !bounded(operator.id, 512) ||
    !isRetryApprovalExecutionCommand(command)
  ) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = frozen(operator, command, "CLAIMED");
  const next = [...pendingByIdentity.values(), recovery];
  if (!saveAll(next)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingByIdentity.set(identityKey(operator), recovery);
  return { status: "OWNED", recovery };
}

function exactDurable(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  hydrate();
  const key = identityKey(operator);
  if (
    unavailable ||
    pendingByIdentity.get(key) !== recovery ||
    !belongsTo(recovery, operator) ||
    (typeof window !== "undefined" && !storage())
  ) {
    unavailable = true;
    return false;
  }
  if (typeof window === "undefined") return true;
  try {
    const target = storage();
    const stored = target ? readStored(target) : null;
    if (stored?.some((candidate) => same(candidate, recovery))) return true;
  } catch {
    // Fall through to the fail-closed result.
  }
  unavailable = true;
  return false;
}

export function beginApprovalExecutionDispatch(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  const key = identityKey(operator);
  if (dispatchingByIdentity.has(key) || !exactDurable(recovery, operator)) return false;
  dispatchingByIdentity.set(key, recovery);
  return true;
}

export function finishApprovalExecutionDispatch(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  const key = identityKey(operator);
  if (dispatchingByIdentity.get(key) !== recovery || !belongsTo(recovery, operator)) return false;
  dispatchingByIdentity.delete(key);
  return true;
}

function updateState(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
  state: ApprovalExecutionRecoveryState,
): boolean {
  if (!exactDurable(recovery, operator)) return false;
  const updated = recovery.state === state ? recovery : withState(recovery, state);
  const key = identityKey(operator);
  const next = [...pendingByIdentity.values()].map((candidate) =>
    recoveryIdentityKey(candidate) === key ? updated : candidate,
  );
  if (!saveAll(next)) {
    unavailable = true;
    return false;
  }
  pendingByIdentity.set(key, updated);
  return true;
}

export function markApprovalExecutionOutcomeUnknown(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  return updateState(recovery, operator, "OUTCOME_UNKNOWN");
}

export function clearApprovalExecutionRecovery(
  recovery: ApprovalExecutionRecovery,
  operator: { tenantId: string; storeId: string; id: string },
): boolean {
  if (!exactDurable(recovery, operator)) return false;
  const key = identityKey(operator);
  const next = [...pendingByIdentity.values()].filter(
    (candidate) => recoveryIdentityKey(candidate) !== key,
  );
  if (!saveAll(next)) {
    unavailable = true;
    return false;
  }
  pendingByIdentity.delete(key);
  return true;
}

export function approvalExecutionCommand(
  recovery: ApprovalExecutionRecovery,
): RetryApprovalExecutionCommand {
  return commandFromRecovery(recovery);
}

export function approvalExecutionResultIsTerminal(approval: ApprovalRequestReadModel): boolean {
  return (
    approval.execution.status === "SUCCEEDED" ||
    approval.execution.status === "BLOCKED_PRECONDITION_CHANGED" ||
    approval.execution.status === "BLOCKED_INTEGRITY_REVIEW" ||
    approval.execution.status === "FAILED_TERMINAL"
  );
}

export function approvalExecutionOutcomeIsUnknown(error: unknown): boolean {
  const candidate = error as { code?: unknown; statusCode?: unknown } | null;
  const code = typeof candidate?.code === "string" ? candidate.code : null;
  const statusCode = typeof candidate?.statusCode === "number" ? candidate.statusCode : null;
  if (
    code === "GATEWAY_UNREACHABLE" ||
    code === "GATEWAY_RESPONSE_INVALID" ||
    code === "OPERATOR_IDENTITY_CHANGED"
  ) {
    return true;
  }
  if (statusCode === null) return true;
  return statusCode === 408 || statusCode === 429 || statusCode >= 500;
}

export function resetApprovalExecutionRecoveryForTests(): void {
  pendingByIdentity.clear();
  dispatchingByIdentity.clear();
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(APPROVAL_EXECUTION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup resets the in-memory fail-closed state as well.
  }
}

export function resetApprovalExecutionRecoveryMemoryForTests(): void {
  pendingByIdentity.clear();
  dispatchingByIdentity.clear();
  hydrated = false;
  unavailable = false;
}
